/**
 * OP-3605 — a estrutura do menu do BD Interno.
 *
 * Fora do layout de propósito: o que aparece no menu é REGRA (cada item tem
 * sua permissão, e um grupo sem item visível não deve aparecer), e regra se
 * testa sem montar tela. Mesmo espírito dos `regras.ts` das páginas.
 *
 * Os dezenove itens vinham soltos num nível só. Agrupados pelo que a pessoa
 * está fazendo — pedir, olhar o patrimônio, movimentar, conferir — o menu
 * fecha os grupos que ela não usa e cabe na tela de um notebook.
 */

export type IconeMenu =
  | 'ordem' | 'importacao' | 'tecnicos' | 'analytics' | 'config'
  | 'hoje' | 'fila' | 'remessas' | 'contratos' | 'historico' | 'minhas'
  | 'posicao' | 'equipamentos' | 'razao' | 'catalogo'
  | 'entrada' | 'transferencias' | 'baixas'
  | 'inventario' | 'ocorrencias'
  | 'notificacoes'
  | 'carteira' | 'lotes' | 'gmv' | 'qualidade' | 'estudo' | 'simulador'
  | 'mais'

export interface ItemMenu {
  to: string
  rotulo: string
  icone: IconeMenu
  /** Rotas que pertencem a este item e não são prefixo de `to`. */
  tambem?: string[]
  /**
   * Rotas que caem sob `to` mas são de OUTRO item. Sem isto,
   * `/interno/os/importacao` acenderia "Ordens de Serviço" junto com
   * "Acompanhar importações" — dois itens do mesmo grupo destacados.
   */
  excecoes?: string[]
  /**
   * Só a rota exata acende o item. É o caso da porta do módulo
   * (`/interno/estoque`): como prefixo ela cobriria todas as telas de estoque.
   */
  exato?: boolean
  /** Item com contador próprio; o layout o desenha à parte. */
  contador?: 'importacoes'
}

export interface GrupoMenu {
  id: string
  titulo: string
  itens: ItemMenu[]
}

/** As permissões que decidem o menu, já resolvidas pelo layout. */
export interface AcessosMenu {
  importacoes: boolean
  analytics: boolean
  tecnicos: boolean
  config: boolean
  estoque: boolean
  solicitar: boolean
  patrimonio: boolean
  notificacoes: boolean
  previsoes: boolean
}

/** Inclui o item só quando a permissão existe, sem `false` no meio da lista. */
const se = (permitido: boolean, item: ItemMenu): ItemMenu[] => (permitido ? [item] : [])

/**
 * O menu de quem está logado: só os itens permitidos, e só os grupos que
 * sobraram com algum item. Quem tem apenas `ESL2` recebe "Solicitações" com
 * uma linha, em vez de procurar "Minhas solicitações" no meio de dezenove.
 */
export function montarMenu(a: AcessosMenu): GrupoMenu[] {
  const grupos: GrupoMenu[] = [
    {
      id: 'operacoes',
      titulo: 'Operações',
      itens: [
        {
          to: '/interno/os',
          rotulo: 'Ordens de Serviço',
          icone: 'ordem',
          excecoes: ['/interno/os/importacao'],
        },
        ...se(a.importacoes, {
          to: '/interno/os/importacao',
          rotulo: 'Acompanhar importações',
          icone: 'importacao',
          contador: 'importacoes',
        }),
        ...se(a.tecnicos, { to: '/interno/tecnicos', rotulo: 'Técnicos', icone: 'tecnicos' }),
        ...se(a.analytics, { to: '/interno/analytics', rotulo: 'Analytics de Operações', icone: 'analytics' }),
        ...se(a.config, { to: '/interno/config', rotulo: 'Configuração', icone: 'config' }),
      ],
    },
    {
      id: 'solicitacoes',
      titulo: 'Solicitações',
      itens: [
        ...se(a.estoque, { to: '/interno/estoque', rotulo: 'Hoje', icone: 'hoje', exato: true }),
        ...se(a.estoque, { to: '/interno/estoque/fila', rotulo: 'Fila', icone: 'fila' }),
        ...se(a.estoque, { to: '/interno/estoque/remessas', rotulo: 'Remessas', icone: 'remessas' }),
        ...se(a.estoque, { to: '/interno/estoque/vinculos', rotulo: 'Contratos', icone: 'contratos' }),
        ...se(a.estoque, { to: '/interno/estoque/solicitacoes', rotulo: 'Histórico', icone: 'historico' }),
        ...se(a.solicitar, {
          to: '/interno/estoque/minhas',
          rotulo: 'Minhas solicitações',
          icone: 'minhas',
          // A tela de abrir pedido não tem item próprio: entra-se nela pelo
          // botão de "Minhas solicitações", então é este item que ela acende.
          tambem: ['/interno/estoque/solicitar'],
        }),
        ...se(a.notificacoes, { to: '/interno/estoque/notificacoes', rotulo: 'Notificações', icone: 'notificacoes' }),
      ],
    },
    {
      id: 'patrimonio',
      titulo: 'Patrimônio',
      itens: [
        // "Estoque" virou "Posição": dentro de um menu de estoque, o rótulo
        // antigo não dizia nada.
        ...se(a.estoque, { to: '/interno/estoque/posicao', rotulo: 'Posição', icone: 'posicao' }),
        ...se(a.estoque, { to: '/interno/estoque/equipamentos', rotulo: 'Equipamentos', icone: 'equipamentos' }),
        ...se(a.estoque, { to: '/interno/estoque/razao', rotulo: 'Razão', icone: 'razao' }),
        ...se(a.estoque, { to: '/interno/estoque/catalogo', rotulo: 'Catálogo', icone: 'catalogo' }),
      ],
    },
    {
      id: 'movimentacao',
      titulo: 'Movimentação',
      itens: [
        ...se(a.patrimonio, { to: '/interno/estoque/entrada', rotulo: 'Entrada', icone: 'entrada' }),
        ...se(a.estoque, { to: '/interno/estoque/transferencias', rotulo: 'Transferências', icone: 'transferencias' }),
        ...se(a.estoque, { to: '/interno/estoque/baixas', rotulo: 'Baixas', icone: 'baixas' }),
      ],
    },
    {
      id: 'conferencia',
      titulo: 'Conferência',
      itens: [
        ...se(a.estoque, { to: '/interno/estoque/inventarios', rotulo: 'Inventário', icone: 'inventario' }),
        ...se(a.estoque, { to: '/interno/estoque/ocorrencias', rotulo: 'Ocorrências', icone: 'ocorrencias' }),
      ],
    },
    {
      id: 'previsoes',
      titulo: 'Previsões',
      itens: [
        ...se(a.previsoes, {
          to: '/interno/previsoes',
          rotulo: 'Carteira',
          icone: 'carteira',
          // o detalhe do evento (/interno/previsoes/evento/:id) acende a Carteira
          excecoes: [
            '/interno/previsoes/lotes',
            '/interno/previsoes/gmv',
            '/interno/previsoes/qualidade',
            '/interno/previsoes/estudo',
            '/interno/previsoes/simulador',
          ],
        }),
        ...se(a.previsoes, { to: '/interno/previsoes/lotes', rotulo: 'Lotes em risco', icone: 'lotes' }),
        ...se(a.previsoes, { to: '/interno/previsoes/gmv', rotulo: 'GMV da BD', icone: 'gmv' }),
        ...se(a.previsoes, { to: '/interno/previsoes/simulador', rotulo: 'Simulador de evento', icone: 'simulador' }),
        ...se(a.previsoes, { to: '/interno/previsoes/qualidade', rotulo: 'Qualidade do modelo', icone: 'qualidade' }),
        ...se(a.previsoes, { to: '/interno/previsoes/estudo', rotulo: 'Como o público compra', icone: 'estudo' }),
      ],
    },
  ]
  return grupos.filter((g) => g.itens.length > 0)
}

/** Prefixo só vale em borda de segmento: `/interno/os` não cobre `/interno/oscar`. */
function cobre(rota: string, caminho: string): boolean {
  return caminho === rota || caminho.indexOf(rota + '/') === 0
}

/** `/interno/estoque/` e `/interno/estoque` são a mesma tela. */
export const normalizarCaminho = (caminho: string): string => caminho.replace(/\/+$/, '') || '/'

/**
 * O item aceso. Também usado no lugar do `isActive` do NavLink, que não sabe
 * das exceções e acenderia dois itens do mesmo grupo.
 */
export function itemAtivo(item: ItemMenu, caminhoBruto: string): boolean {
  const caminho = normalizarCaminho(caminhoBruto)
  const excecoes = item.excecoes || []
  for (let i = 0; i < excecoes.length; i += 1) {
    if (cobre(excecoes[i], caminho)) return false
  }
  const rotas = [item.to].concat(item.tambem || [])
  if (item.exato) return rotas.indexOf(caminho) >= 0
  for (let i = 0; i < rotas.length; i += 1) {
    if (cobre(rotas[i], caminho)) return true
  }
  return false
}

/**
 * O grupo da rota atual, para o layout abri-lo sozinho — ninguém deveria
 * precisar caçar em qual seção está a tela aberta. Rota mais longa ganha,
 * então `/interno/estoque/fila/7` resolve pela fila, não por um prefixo
 * mais curto de outro grupo.
 */
export function grupoDaRota(grupos: GrupoMenu[], caminhoBruto: string): string | null {
  const caminho = normalizarCaminho(caminhoBruto)
  let id: string | null = null
  let tamanho = -1
  for (let g = 0; g < grupos.length; g += 1) {
    const itens = grupos[g].itens
    for (let i = 0; i < itens.length; i += 1) {
      if (!itemAtivo(itens[i], caminho)) continue
      const rotas = [itens[i].to].concat(itens[i].tambem || [])
      for (let r = 0; r < rotas.length; r += 1) {
        if (cobre(rotas[r], caminho) && rotas[r].length > tamanho) {
          tamanho = rotas[r].length
          id = grupos[g].id
        }
      }
    }
  }
  return id
}

/** OP-3619 — a porta do resto do menu no celular. */
export const ROTA_MAIS = '/interno/estoque/mais'

const ABA_MAIS: ItemMenu = { to: ROTA_MAIS, rotulo: 'Mais', icone: 'mais', exato: true }

/** As quatro telas que a logística abre o dia inteiro, na ordem do dia. */
function abasBase(a: AcessosMenu): ItemMenu[] {
  return [
    ...se(a.estoque, { to: '/interno/estoque', rotulo: 'Hoje', icone: 'hoje', exato: true }),
    ...se(a.estoque, { to: '/interno/estoque/fila', rotulo: 'Fila', icone: 'fila' }),
    ...se(a.estoque, { to: '/interno/estoque/remessas', rotulo: 'Remessas', icone: 'remessas' }),
    ...se(a.solicitar, {
      to: '/interno/estoque/minhas',
      rotulo: 'Minhas',
      icone: 'minhas',
      tambem: ['/interno/estoque/solicitar'],
    }),
  ]
}

/**
 * O menu que sobra para a tela "Mais": tudo que a pessoa pode abrir e não
 * virou aba. Grupo sem item some, como no menu lateral.
 */
export function gruposDoMais(a: AcessosMenu): GrupoMenu[] {
  const nasAbas = abasBase(a).map((i) => i.to)
  return montarMenu(a)
    .map((g) => ({ ...g, itens: g.itens.filter((i) => nasAbas.indexOf(i.to) < 0) }))
    .filter((g) => g.itens.length > 0)
}

/**
 * A barra de abas do celular. Só existe com pelo menos uma tela de estoque,
 * e só ganha "Mais" quando há algo em Mais — aba que abre uma tela vazia é
 * pior que aba nenhuma.
 */
export function abasMobile(a: AcessosMenu): ItemMenu[] {
  const base = abasBase(a)
  if (base.length === 0) return []
  return gruposDoMais(a).length > 0 ? base.concat(ABA_MAIS) : base
}

/** Telas de formulário: a barra de abas sairia por cima da ação da tela. */
const SEM_ABAS = [
  '/interno/estoque/solicitar',
  '/interno/estoque/entrada',
  '/interno/estoque/transferencias/nova',
]

/**
 * A barra de abas aparece nas telas-raiz do Estoque. Some no detalhe
 * (`/fila/7`) e nos formulários, onde a ação da própria tela ocupa o rodapé.
 */
export function mostrarAbas(caminhoBruto: string): boolean {
  const caminho = normalizarCaminho(caminhoBruto)
  if (!cobre('/interno/estoque', caminho)) return false
  if (SEM_ABAS.indexOf(caminho) >= 0) return false
  return !/\/\d+$/.test(caminho)
}
